import Add from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import type { FC } from "react";
import { FONT_SIZE, ICON_SIZE, INK, MOTION, NEUTRAL, VISUAL_CHOICE } from "../../../theme/tokens";

type DashedAddButtonProps = {
	/** Already translated — what the press adds ("Add tab", "Add time group"). */
	label: string;
	onClick: () => void;
	/** Greyed out and inert — e.g. the list reached its limit (pass the limit text as `label`). */
	disabled: boolean;
	dataTestId: string;
};

/**
 * The "add another" action at the foot of an editable list inside a settings panel: a small dashed
 * outline that reads as an empty slot waiting to be filled, so it never competes with the filled items
 * above it. A real `<button>` — keyboard-operable, with the visible label as its name.
 */
export const DashedAddButton: FC<DashedAddButtonProps> = ({ label, onClick, disabled, dataTestId }) => (
	<Box
		component="button"
		type="button"
		onClick={onClick}
		disabled={disabled}
		data-testid={dataTestId}
		sx={(theme) => ({
			display: "inline-flex",
			alignItems: "center",
			alignSelf: "flex-start",
			gap: 0.5,
			px: 1,
			py: 0.5,
			bgcolor: "transparent",
			border: `1px dashed ${NEUTRAL[400]}`,
			borderRadius: 1,
			cursor: "pointer",
			color: theme.palette.primary.dark,
			fontSize: FONT_SIZE.CAPTION,
			fontFamily: "inherit",
			"& > svg": { fontSize: ICON_SIZE.MICRO },
			transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
			"@media (hover: hover) and (pointer: fine)": { "&:hover:not(:disabled)": { bgcolor: NEUTRAL[100] } },
			"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
			"&:active:not(:disabled)": { transform: `scale(${VISUAL_CHOICE.PRESS_SCALE})` },
			"&:disabled": { cursor: "default", color: INK.DISABLED, borderColor: NEUTRAL[300] },
			"@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active:not(:disabled)": { transform: "none" } },
		})}
	>
		<Add />
		{label}
	</Box>
);
